package com.tovtalk.data.repository

import com.lomdaat.ads.api.AdsClient
import com.lomdaat.ads.model.AdsIdentity
import com.lomdaat.ads.model.AppId
import com.lomdaat.ads.model.BannerAdRequest
import com.lomdaat.ads.model.BannerSize
import com.lomdaat.ads.model.InterstitialAdRequest
import com.lomdaat.ads.model.NativeAd
import com.lomdaat.ads.model.NativeAdRequest
import com.lomdaat.ads.model.VideoAd
import com.lomdaat.ads.model.VideoAdTagRequest
import com.lomdaat.ads.platform.AdsIdentityProvider
import com.tovtalk.domain.repository.AdsRepository
import com.tovtalk.domain.repository.InterstitialAd
import com.tovtalk.utils.logger.Logger
import io.ktor.client.HttpClient
import io.ktor.client.request.get
import io.ktor.client.statement.bodyAsText

private const val APP_ID = "com.lomdaat.tovtalk"

class NoOpAdsIdentityProvider : AdsIdentityProvider {
    override suspend fun getAdsIdentity() = AdsIdentity(
        requestId = null,
        trackingAllowed = false,
    )
}

class AdsRepositoryImpl(
    private val adsClient: AdsClient,
    private val httpClient: HttpClient,
    private val identityProvider: AdsIdentityProvider,
    private val feedAdsEnabledForGuests: Boolean,
    private val profileAdsEnabledForGuests: Boolean,
) : AdsRepository {

    override suspend fun shouldShowFeedAds(showAdsFlag: Boolean?, isGuest: Boolean): Boolean =
        if (isGuest) feedAdsEnabledForGuests else showAdsFlag == true

    override suspend fun shouldShowProfileAds(showAdsFlag: Boolean?, isGuest: Boolean): Boolean =
        if (isGuest) profileAdsEnabledForGuests else showAdsFlag == true

    override suspend fun fetchNativeAd(): NativeAd? = runCatching {
        val identity = identityProvider.getAdsIdentity()
        adsClient.fetchNativeAd(
            NativeAdRequest(
                appId = AppId(APP_ID),
                requestId = identity.requestId,
            )
        )
    }.getOrElse { e ->
        Logger.e("fetchNativeAd failed: ${e.message}", "AdsRepositoryImpl", e)
        null
    }

    override suspend fun fetchInterstitialAd(): InterstitialAd? = runCatching {
        val url = buildInterstitialUrl()
        val html = httpClient.get(url).bodyAsText()
        parseInterstitialHtml(html)
    }.getOrElse { e ->
        Logger.e("fetchInterstitialAd failed: ${e.message}", "AdsRepositoryImpl", e)
        null
    }

    override suspend fun fetchBannerAd(): InterstitialAd? = runCatching {
        val url = buildBannerUrl()
        val html = httpClient.get(url).bodyAsText()
        parseBannerHtml(html)
    }.getOrElse { e ->
        Logger.e("fetchBannerAd failed: ${e.message}", "AdsRepositoryImpl", e)
        null
    }

    override suspend fun buildBannerUrl(): String {
        val identity = identityProvider.getAdsIdentity()
        return adsClient.buildBannerUrl(
            BannerAdRequest(
                appId = AppId(APP_ID),
                requestId = identity.requestId,
                size = BannerSize.BANNER,
            )
        )
    }

    override suspend fun fetchVerticalVideoAd(): VideoAd? = runCatching {
        val identity = identityProvider.getAdsIdentity()
        adsClient.fetchVerticalVideoAd(
            VideoAdTagRequest(
                appId = AppId(APP_ID),
                requestId = identity.requestId,
            )
        )
    }.getOrElse { e ->
        Logger.e("fetchVerticalVideoAd failed: ${e.message}", "AdsRepositoryImpl", e)
        null
    }

    override suspend fun trackVideoAdEvent(url: String): Boolean =
        runCatching { adsClient.trackEvent(url) }.getOrDefault(false)

    override suspend fun buildInterstitialUrl(): String {
        val identity = identityProvider.getAdsIdentity()
        return adsClient.buildInterstitialUrl(
            InterstitialAdRequest(
                appId = AppId(APP_ID),
                requestId = identity.requestId,
            )
        )
    }
}

private val jsUrlRegex = Regex("""let\s+url\s*=\s*"((?:[^"\\]|\\.)*)"""")
private val jsBannerUrlRegex = Regex("""let\s+bannerUrl\s*=\s*"((?:[^"\\]|\\.)*)"""")
private val jsImpressionRegex = Regex("""let\s+impressionUrl\s*=\s*"((?:[^"\\]|\\.)*)"""")
private val jsClickRegex = Regex("""let\s+clickUrl\s*=\s*"((?:[^"\\]|\\.)*)"""")
private val imgSrcRegex = Regex("""<img[^>]+src="([^"]+)"""")

private fun unescapeJs(s: String) = s.replace("\\/", "/")
    .replace("\\u0026", "&")

private fun parseAdHtml(html: String, urlRegex: Regex): InterstitialAd? {
    val imageUrl = imgSrcRegex.find(html)?.groupValues?.get(1)
        ?.replace("&amp;", "&") ?: return null
    val clickThroughUrl = urlRegex.find(html)?.groupValues?.get(1)
        ?.let(::unescapeJs) ?: return null
    val impressionUrl = jsImpressionRegex.find(html)?.groupValues?.get(1)
        ?.let(::unescapeJs)
    val clickTrackingUrl = jsClickRegex.find(html)?.groupValues?.get(1)
        ?.let(::unescapeJs)
    return InterstitialAd(
        imageUrl = imageUrl,
        clickThroughUrl = clickThroughUrl,
        impressionUrl = impressionUrl,
        clickTrackingUrl = clickTrackingUrl,
    )
}

private fun parseInterstitialHtml(html: String) = parseAdHtml(html, jsUrlRegex)
private fun parseBannerHtml(html: String) = parseAdHtml(html, jsBannerUrlRegex)
